import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const BoolBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $val }) => $val ? '#2e7d3215' : '#9e9e9e15'};
  color: ${({ $val }) => $val ? '#2e7d32' : '#9e9e9e'};
`;

export const useBulkLeaveApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    // Uç SUNUCU TARAFINDA sayfalar ve süzer (BulkLeaveController.GetAll → PaginatedList):
    // pageNumber/pageSize, orderBy/orderDir, leaveStart/leaveEnd [between|gte|lte] (kiracının
    // yerel günü), cost[gte], isHourly[eq], isPaid[eq]. Eskiden düz dizi dönüyor ve bu
    // parametrelerin hepsi yok sayılıyordu; toplam/sayfa artık sunucudan gelir.
    api.get(`/v1/bulk-leaves?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        setData(result?.data || []);
        setPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageIndex: (result?.pageIndex || 1) - 1,
          pageSize: result?.pageSize || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    dateFilterColumn('leaveStart', t('bulkLeave.leaveStart'), lang),
    dateFilterColumn('leaveEnd', t('bulkLeave.leaveEnd'), lang),
    // Arka uç yalnız `cost[gte]` (decimal?) okur. Ham metin süzgeci "1500,50"yi olduğu gibi
    // gönderiyordu → 400 → tablo sessizce boş; "1.500" → 1,5. Yerel biçim çözülür, değişmez
    // biçimde (`1500.5`) gönderilir; yalnız alt sınır sunulur.
    numericFilterColumn('cost', t('bulkLeave.cost'), { decimals: 2, sort: false, minOnly: true, locale: lang }),
    {
      name: 'isHourly', label: t('bulkLeave.isHourly'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => <BoolBadge $val={v}>{v ? t('common.yes') : t('common.no')}</BoolBadge>,
      },
    },
    {
      name: 'isPaid', label: t('bulkLeave.isPaid'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => <BoolBadge $val={v}>{v ? t('common.yes') : t('common.no')}</BoolBadge>,
      },
    },
    { name: 'leaveCount', label: t('bulkLeave.leaveCount'), options: { filter: false, sort: false } },
    { name: 'createDate', label: t('bulkLeave.createDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
  ], [t, lang]);

  return { columns, data, fetching, pagination, refresh };
};
